/**
 * Hikvision Pro Series NVR catalog — DS-7100, DS-7600, DS-7700 families.
 *
 * Every entry below is verified against a primary Hikvision datasheet PDF or
 * the official product page (regional subdomain). Each block cites its source.
 *
 * Coverage:
 *   • DS-7100NI-Q1/M       — Value 7 entry, no PoE, 4/8-ch (legacy compact 1U)
 *   • DS-7600NI-Q1 (D)     — Pro Q, no PoE, 4/8-ch
 *   • DS-7600NI-K1/4P/4G   — Pro K w/ PoE + 4G modem, 4/8-ch
 *   • DS-7600NI-K1/4P (D)  — Pro K w/ PoE, 4-ch
 *   • DS-7608NI-K2 (D)     — Pro K, no PoE, 8-ch 2-bay
 *   • DS-7600NXI-K1/4P (D) — Pro K AcuSense w/ PoE, 4-ch 1-bay
 *   • DS-7600NXI-K1 (B)    — Pro K AcuSense, no PoE, 8-ch 1-bay
 *   • DS-7600NXI-K1/8P (B) — Pro K AcuSense w/ PoE, 8-ch 1-bay
 *   • DS-7600NXI-K2/16P    — Pro K AcuSense w/ PoE, 16-ch 2-bay
 *   • DS-7600NXI-I2/S (E)  — Pro I AcuSense, no PoE, 8-ch 2-bay
 *   • DS-7700NI-K4         — Pro K, no PoE, 8/16/32-ch 4-bay (1.5U)
 *   • DS-7700NI-K4/P       — Pro K w/ PoE, 8/16/32-ch 4-bay (1.5U)
 *   • DS-7700NI-I4/P (B)   — Pro I, w/ PoE, 16/32-ch 4-bay (1.5U)
 *   • DS-7700NXI-K4        — Pro K AcuSense, no PoE, 16/32-ch 4-bay (1.5U)
 *   • DS-7700NXI-K4/P      — Pro K AcuSense w/ PoE, 8/16/32-ch 4-bay (1.5U)
 *
 * Field policy:
 *   • formFactor "1.5U" → '2U' (closest enum value).
 *   • channels = IP camera input count.
 *   • Bandwidth values are H.265-mode figures from the datasheet.
 *   • Network-port string captures speed and count.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const HIKVISION_NVR_PRO: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // Value 7 — DS-7100NI-Q1/M (compact 1U, 10/100 Mbps WAN, no PoE)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7104ni-q1-m',
    manufacturer: 'Hikvision',
    model: 'DS-7104NI-Q1/M',
    category: 'nvr-recorder',
    tagline: '4-ch Value 7 NVR, 1 HDD up to 6 TB, 4 MP, 10/100 LAN (no PoE)',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000000621/Datasheet-of-DS-7108NI-Q1_M_Value-7-Series-NVR_V4.73.200_20230404.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 60,
      maxResolution: '6 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7108ni-q1-m',
    manufacturer: 'Hikvision',
    model: 'DS-7108NI-Q1/M(D)',
    category: 'nvr-recorder',
    tagline: '8-ch Value 7 NVR, 1 HDD up to 6 TB, 4 MP, 10/100 LAN (no PoE)',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000000621/Datasheet-of-DS-7108NI-Q1_M_Value-7-Series-NVR_V4.73.200_20230404.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 60,
      outputBandwidthMbps: 60,
      maxResolution: '6 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series Q — DS-7600NI-Q1 (no PoE, 1-bay, older Pro-Q lineup)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7604ni-q1-d',
    manufacturer: 'Hikvision',
    model: 'DS-7604NI-Q1(D)',
    category: 'nvr-recorder',
    tagline: '4-ch Pro Q NVR, 1 HDD up to 8 TB, 8 MP, 4K HDMI (no PoE)',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000000561/Datasheet-of-DS-7604NI-Q1-NVRD_V4.71.200_20221031.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 8,
      poeInPorts: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out (optional /ALARM model)',
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7608ni-q1-d',
    manufacturer: 'Hikvision',
    model: 'DS-7608NI-Q1(D)',
    category: 'nvr-recorder',
    tagline: '8-ch Pro Q NVR, 1 HDD up to 8 TB, 8 MP, 4K HDMI (no PoE)',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000000569/Datasheet-of-DS-7608NI-Q1-NVRD_V4.71.200_20221031.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 8,
      poeInPorts: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out (optional /ALARM model)',
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series K — DS-7600NI-K1/P (with PoE, 1-bay)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7604ni-k1-4p-d',
    manufacturer: 'Hikvision',
    model: 'DS-7604NI-K1/4P(D)',
    category: 'nvr-recorder',
    tagline: '4-ch Pro K NVR, 4-port PoE (50 W), 1 HDD up to 8 TB, 4K HDMI',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000019898/Datasheet-of-DS-7604NI-K1_4P-NVRD_V4.71.200_20221031.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 8,
      poeInPorts: 4,
      poeBudgetW: 50,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out (optional /ALARM model)',
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series K — DS-7600NI-K1/P/4G (with PoE + 4G LTE modem)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7604ni-k1-4p-4g',
    manufacturer: 'Hikvision',
    model: 'DS-7604NI-K1/4P/4G',
    category: 'nvr-recorder',
    tagline: '4-ch Pro K NVR with 4G LTE, 4-port PoE (50 W), 1 HDD up to 6 TB',
    verified: true,
    datasheetUrl: 'https://specsheets.dvs.co.uk/DS-7604NI-K14P4G.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 4,
      poeBudgetW: 50,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45 + 4G SIM/UIM slot (GSM/WCDMA/LTE)',
    },
  },
  {
    id: 'hikvision-ds-7608ni-k1-8p-4g',
    manufacturer: 'Hikvision',
    model: 'DS-7608NI-K1/8P/4G',
    category: 'nvr-recorder',
    tagline: '8-ch Pro K NVR with 4G LTE, 8-port PoE (75 W), 1 HDD up to 6 TB',
    verified: true,
    datasheetUrl: 'https://specsheets.dvs.co.uk/DS-7604NI-K14P4G.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 8,
      poeBudgetW: 75,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45 + 4G SIM/UIM slot (GSM/WCDMA/LTE)',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series K — DS-7608NI-K2 (no PoE, 2-bay)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7608ni-k2-d',
    manufacturer: 'Hikvision',
    model: 'DS-7608NI-K2(D)',
    category: 'nvr-recorder',
    tagline: '8-ch Pro K 4K NVR, 2 HDD up to 10 TB, 4/1 alarm I/O (no PoE)',
    verified: true,
    datasheetUrl: 'https://hikvision.zone/wp-content/uploads/2022/12/Datasheet-of-DS-7608NI-K2-NVR-D-or-E_4.71.410_Hikvision-Zone.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 160,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series K AcuSense — DS-7600NXI-K1 / K1/xP (1-bay)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7604nxi-k1-b',
    manufacturer: 'Hikvision',
    model: 'DS-7604NXI-K1 (B)',
    category: 'nvr-recorder',
    tagline: '4-ch K-Series AcuSense 4K NVR, 1 HDD up to 16 TB (no PoE)',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000042/M000058881/Data_Sheet/Datasheet-of-DS-7604NXI-K1_NVRB_V4.74.000_20230209.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out (optional /Alarm4+1)',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7604nxi-k1-4p-d',
    manufacturer: 'Hikvision',
    model: 'DS-7604NXI-K1/4P(D)',
    category: 'nvr-recorder',
    tagline: '4-ch K AcuSense 4K NVR, 4-port PoE (50 W), 1 HDD up to 16 TB',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000141839/Datasheet-of-DS-7604NXI-K1_4PD_20241025.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 4,
      poeBudgetW: 50,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out (optional /Alarm4+1)',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7608nxi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7608NXI-K1',
    category: 'nvr-recorder',
    tagline: '8-ch K-Series AcuSense 4K NVR, 1 HDD up to 10 TB (no PoE)',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000000007/S000000026/OFR000042/M000058879/Data_Sheet/Datasheet-of-DS-7608NXI-K1_V4.71.100_20221012.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out (optional /Alarm4+1)',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7608nxi-k1-8p-b',
    manufacturer: 'Hikvision',
    model: 'DS-7608NXI-K1/8P (B)',
    category: 'nvr-recorder',
    tagline: '8-ch K AcuSense 4K NVR, 8-port PoE (75 W), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000042/M000058878/Data_Sheet/Datasheet-of-DS-7608NXI-K1_8P_NVRB_V4.74.000_20230209.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 75,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out (optional /Alarm4+1)',
      networkPorts: '1× 10/100 Mbps RJ-45 LAN (PoE switch is 10/100 Mbps)',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series K AcuSense — DS-7616NXI-K2/16P (2-bay, 16-ch)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7616nxi-k2-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7616NXI-K2/16P',
    category: 'nvr-recorder',
    tagline: '16-ch K AcuSense 4K NVR, 16-port PoE (200 W), 2 HDD up to 10 TB',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000000007/S000000026/OFR031212/M000058884/Data_Sheet/Datasheet-of-DS-7616NXI-K2_16P_V4.74.000_20230209.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out (optional /Alarm4+1)',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series I AcuSense — DS-7608NXI-I2/S (2-bay, w/ PoE)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7608nxi-i2-8p-s-e',
    manufacturer: 'Hikvision',
    model: 'DS-7608NXI-I2/8P/S (E)',
    category: 'nvr-recorder',
    tagline: '8-ch I-Series AcuSense 4K NVR, 8-port PoE (150 W), 2 HDD up to 10 TB',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/pl/firmware/DS-7608NXI-I2_8P_SE_Datasheet_20250114_PL.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 150,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out (optional)',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series K — DS-7700NI-K4 (no PoE, 4-bay, 1.5U)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7708ni-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7708NI-K4',
    category: 'nvr-recorder',
    tagline: '8-ch Pro K 4K NVR, 4 HDD up to 10 TB, 16/4 alarm (no PoE)',
    verified: true,
    datasheetUrl: 'https://www.megateh.eu/files/hikvisionv/hik8/datasheet-ds-7716ni-k4.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7732ni-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7732NI-K4',
    category: 'nvr-recorder',
    tagline: '32-ch Pro K 4K NVR, 4 HDD up to 10 TB, 16/4 alarm (no PoE)',
    verified: true,
    datasheetUrl: 'https://www.megateh.eu/files/hikvisionv/hik8/datasheet-ds-7716ni-k4.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out (16/9 optional)',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series K — DS-7700NI-K4/P (with PoE, 4-bay)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7708ni-k4-8p',
    manufacturer: 'Hikvision',
    model: 'DS-7708NI-K4/8P',
    category: 'nvr-recorder',
    tagline: '8-ch Pro K 4K NVR, 8-port PoE (200 W), 4 HDD up to 10 TB',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000000588/Datasheet-of-DS-7708NI-K4_8P-NVR-D-or-E_4.71.410_20230920.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 200,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7732ni-k4-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7732NI-K4/16P',
    category: 'nvr-recorder',
    tagline: '32-ch Pro K 4K NVR, 16-port PoE (200 W), 4 HDD up to 10 TB',
    verified: true,
    datasheetUrl: 'https://www.getic.com/files/catalogue/3096/datasheetofds7732nik4_16pnvre_471410_20221026-6384bc1fb0a32.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out (16/9 optional)',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series I — DS-7700NI-I4/P (legacy AcuSense I-Series, w/ PoE)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7716ni-i4-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7716NI-I4/16P',
    category: 'nvr-recorder',
    tagline: '16-ch I-Series 4K NVR, 16-port PoE (200 W), 4 HDD up to 6 TB',
    verified: true,
    datasheetUrl: 'https://www.serioussecurity.com.au/user_manuals/HIK-7732NI-I4-16Datasheet.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 6,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7732ni-i4-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7732NI-I4/16P',
    category: 'nvr-recorder',
    tagline: '32-ch I-Series 4K NVR, 16-port PoE (200 W), 4 HDD up to 6 TB',
    verified: true,
    datasheetUrl: 'https://www.serioussecurity.com.au/user_manuals/HIK-7732NI-I4-16Datasheet.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 6,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series K AcuSense — DS-7700NXI-K4 (no PoE, 4-bay, 1.5U)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7716nxi-k4-b',
    manufacturer: 'Hikvision',
    model: 'DS-7716NXI-K4',
    category: 'nvr-recorder',
    tagline: '16-ch K AcuSense 4K NVR, 4 HDD up to 10 TB, 16/4 alarm (no PoE)',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000059582/Datasheet-of-DS-7716NXI-K4_V4.74.000_20230104.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out (16/9 optional)',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series K AcuSense — DS-7700NXI-K4/P (with PoE)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7708nxi-k4-8p',
    manufacturer: 'Hikvision',
    model: 'DS-7708NXI-K4/8P',
    category: 'nvr-recorder',
    tagline: '8-ch K AcuSense 4K NVR, 8-port PoE (150 W), 4 HDD up to 16 TB',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/normal/all/doc/m000160266/DS-7708NXI-K4_8P_VPro_Datasheet_20250925.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 16,
      poeInPorts: 8,
      poeBudgetW: 150,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7716nxi-k4-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7716NXI-K4/16P',
    category: 'nvr-recorder',
    tagline: '16-ch K AcuSense 4K NVR, 16-port PoE (200 W), 4 HDD up to 10 TB',
    verified: true,
    datasheetUrl: 'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000000007/S000000026/OFR000042/M000059585/Data_Sheet/Datasheet-of-DS-7716NXI-K4_16P_V4.74.000_20230104.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out (16/9 optional)',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7732nxi-k4-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7732NXI-K4/16P',
    category: 'nvr-recorder',
    tagline: '32-ch K AcuSense 4K NVR, 16-port PoE (200 W), 4 HDD up to 10 TB',
    verified: true,
    datasheetUrl: 'https://assets.hikvision.com/prd/public/all/doc/m000059586/Datasheet-of-DS-7732NXI-K4_16P_V4.74.000_20230104.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out (16/9 optional)',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series I AcuSense — DS-7700NXI-I4/16P/S (legacy DeepinMind I, w/ PoE)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'hikvision-ds-7716nxi-i4-16p-s',
    manufacturer: 'Hikvision',
    model: 'DS-7716NXI-I4/16P/S',
    category: 'nvr-recorder',
    tagline: '16-ch I-Series AcuSense 4K NVR, 16-port PoE (200 W), 4 HDD up to 10 TB',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/lit_files/573207.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },
  {
    id: 'hikvision-ds-7732nxi-i4-16p-s',
    manufacturer: 'Hikvision',
    model: 'DS-7732NXI-I4/16P/S',
    category: 'nvr-recorder',
    tagline: '32-ch I-Series AcuSense 4K NVR, 16-port PoE (200 W), 4 HDD up to 10 TB',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/lit_files/573207.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },
  // ─────────────────────────────────────────────────────────────────────
  // Pro Series I AcuSense — DS-7732NXI-I4/S (non-PoE — pair with external switch)
  // ─────────────────────────────────────────────────────────────────────
  // 32-ch sibling of DS-7732NXI-I4/16P/S without the integrated PoE switch.
  // Lower incoming bandwidth (160 vs 256 Mbps), bigger per-disk cap
  // (16 vs 10 TB), and 2× GbE uplinks instead of 1× — installers pair this
  // with their own PoE switch on a separate VLAN, which is the typical
  // mid-size deployment topology.
  {
    id: 'hikvision-ds-7732nxi-i4-s',
    manufacturer: 'Hikvision',
    model: 'DS-7732NXI-I4/S',
    category: 'nvr-recorder',
    tagline: '32-ch I-Series AcuSense 4K NVR, no built-in PoE, 4 HDD up to 16 TB, dual GbE',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/lit_files/573207.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      poeBudgetW: 0,
      codecs: ['H.265', 'H.265+', 'H.264', 'H.264+'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },
];
